/**
 * The stylesheet from `AI2Aim Landing v2.dc.html`, transcribed.
 *
 * The canvas keeps everything it can in inline `style` attributes and puts
 * only three things in a real stylesheet: document-level rules, the keyframes,
 * and the reduced-motion kill switch. Two of those cannot be expressed inline
 * at all, which is why this file exists rather than the styles living on the
 * elements with the other 392.
 *
 * HOVER — the one translation that is not literal.
 *
 * The canvas marks hover states with a custom `style-hover="…"` attribute that
 * its runtime applies as an inline style while the pointer is down on the
 * element. React has no equivalent, and reproducing it with onMouseEnter state
 * would re-render a section on every pointer cross.
 *
 * So the 33 `style-hover` attributes are collapsed into the 14 distinct rules
 * below. Each one carries `!important`, and that is load-bearing rather than
 * lazy: the reveal observer writes `transform: none` *inline* on every
 * `[data-rv]` element once it finishes animating in, and the capability cards
 * are both `[data-rv]` and hover-lifted. Without `!important` an inline
 * `transform: none` outranks a class rule and the card would never lift.
 * Inline-beats-class is exactly what the canvas runtime does, so `!important`
 * reproduces its precedence rather than fighting it.
 */

/**
 * Google Fonts the design specifies. Injected per-route, matching <helmet>.
 *
 * JetBrains Mono was dropped here on 2026-09-18 when the site consolidated to
 * one family. The request is kept rather than deleted because index.html now
 * loads Manrope too and this one is a duplicate the browser dedupes — but the
 * canvas is meant to be self-contained, and a route that renders it must not
 * depend on the document already having asked for the face.
 */
export const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap";

export const DCV2_CSS = `
html { scroll-behavior: smooth; }
body:has(.dcv2) { margin: 0; background: #F6F3EC; color: #141310; font-family: 'Manrope', sans-serif; -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; }
/* -- Motion tokens --------------------------------------------------------
   The canvas hand-typed five durations and three cubic-beziers that almost
   matched (.22,1,.36,1 / .16,1,.3,1 / .2,.8,.2,1). These are the canonical
   curves, and every interactive transition below resolves from them.

   Built-in CSS easings are too weak to read as deliberate, and the default for
   an unspecified transition is 'ease' -- near-symmetric, so it starts slow at
   exactly the moment the user is watching. Entrances and responses to input
   want ease-out. */
.dcv2 {
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  --ease-in-out: cubic-bezier(0.77, 0, 0.175, 1);
  --dur-press: 140ms;
  --dur-hover: 200ms;
}
/* :where(), so this loses to any utility class rather than beating it.
   As .dcv2 a it scored 0,1,1 and outranked Tailwind's 0,1,0 utilities, which
   meant every shadcn <Button asChild> wrapping a <Link> had its text repainted
   --link: accent text on an accent fill, measured at 1.01:1 on the 404. Plain
   anchors still take the link colour; anchors that say otherwise are believed. */
:where(.dcv2) a { color: var(--link); text-decoration: none; }
:where(.dcv2) a:hover { color: var(--linkh); }
.dcv2 ::selection { background: #38E5FF; color: #141310; }
/* The app's global placeholder rule resolves against a token the dcv2 palette
   does not track, so without this a placeholder keeps its light-mode colour in
   dark mode. The explicit opacity is because Firefox dims placeholders by
   default, which would compound with the alpha already in the colour. */
.dcv2 input::placeholder, .dcv2 textarea::placeholder { color: rgba(var(--ink-rgb),.45); opacity: 1; }
/* The canvas has no focus style at all — it was drawn for a preview frame, not
   for keyboard use. The pages that moved onto this language did have one, so
   without this the port would be an accessibility regression rather than a
   restyle. :focus-visible keeps it off pointer clicks. */
.dcv2 a:focus-visible, .dcv2 button:focus-visible, .dcv2 input:focus-visible, .dcv2 select:focus-visible, .dcv2 textarea:focus-visible { outline: 2px solid var(--acc2); outline-offset: 3px; }
/* Fields from components/forms already draw their own focus treatment on the
   wrapper (.auth-input:focus-within in global.css: a 3px ring plus a 26px
   glow). Stacking the rule above on top of that produced two indicators at
   once - a halo AND a detached, offset outline sitting outside the input's
   own corner radius. The wrapper keeps its border-colour change, which is
   what actually says "focused"; the ring, the glow and the outline all go,
   so focus reads as the field lighting up rather than as a box around it. */
.dcv2 .auth-input:focus-within { box-shadow: none; }
.dcv2 .auth-input input:focus-visible, .dcv2 .auth-input textarea:focus-visible { outline: none; }

@keyframes dcv2-fadeUp { from { opacity: 0; transform: translateY(26px); } to { opacity: 1; transform: translateY(0); } }
@keyframes dcv2-bobA { from { transform: translateY(-7px) rotate(-2.5deg); } to { transform: translateY(8px) rotate(-1deg); } }
@keyframes dcv2-bobB { from { transform: translateY(6px) rotate(2deg); } to { transform: translateY(-9px) rotate(3deg); } }
@keyframes dcv2-bobC { from { transform: translateY(-5px) rotate(1.5deg); } to { transform: translateY(7px) rotate(-1.5deg); } }
@keyframes dcv2-spinSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes dcv2-pulseDot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .45; transform: scale(.8); } }
@keyframes dcv2-cueDrop { 0% { transform: translateY(-100%); } 60% { transform: translateY(0); } 100% { transform: translateY(100%); } }
@keyframes dcv2-sway3d { from { transform: rotateY(-3.4deg) rotateX(1.4deg); } to { transform: rotateY(3.4deg) rotateX(-1.6deg); } }
@keyframes dcv2-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
@keyframes dcv2-progressFill { 0% { transform: scaleX(0); } 62% { transform: scaleX(1); } 100% { transform: scaleX(1); } }
@keyframes dcv2-marqueeY { from { transform: translateY(0); } to { transform: translateY(calc(-50% - 7px)); } }
@keyframes dcv2-gridFlow { from { background-position: 0 0, 0 0; } to { background-position: 0 44px, 0 0; } }

/* Hover, gated. Touch fires :hover on tap and then leaves it stuck until
   the next tap elsewhere, so on a phone every card sat lifted after being
   touched. Press feedback below is what touch gets instead. */
@media (hover: hover) and (pointer: fine) {
  .dcv2 .dc-h-cyan:hover { color: #38E5FF !important; }
  .dcv2 .dc-h-cyan-soft:hover { color: var(--band-acc-h) !important; }
  .dcv2 .dc-h-ink:hover { color: var(--ink) !important; }
  .dcv2 .dc-h-link:hover { color: var(--link) !important; }
  .dcv2 .dc-h-card:hover { transform: translateY(-5px) !important; border-color: rgba(var(--ink-rgb),.4) !important; box-shadow: 0 20px 40px rgba(9,9,7,.1) !important; }
  .dcv2 .dc-h-card-dark:hover { transform: translateY(-5px) !important; box-shadow: 0 22px 44px rgba(9,9,7,.3) !important; }
  .dcv2 .dc-h-btn-sm:hover { color: var(--btnfg) !important; transform: translateY(-2px) !important; box-shadow: 0 10px 24px rgba(9,9,7,.22) !important; }
  .dcv2 .dc-h-btn-lg:hover { color: var(--btnfg) !important; transform: translateY(-3px) !important; box-shadow: 0 16px 34px rgba(9,9,7,.26) !important; }
  .dcv2 .dc-h-btn-cyan:hover { color: var(--band-on-acc) !important; transform: translateY(-3px) !important; box-shadow: 0 18px 40px rgba(var(--band-acc-rgb),.3) !important; }
  .dcv2 .dc-h-ghost:hover { color: var(--ink) !important; border-color: var(--ink) !important; background: rgba(var(--ink-rgb),.04) !important; }
  .dcv2 .dc-h-ghost-light:hover { color: var(--band-ink) !important; border-color: var(--band-ink) !important; background: rgba(var(--band-line-rgb),.06) !important; }
  .dcv2 .dc-h-icon:hover { border-color: var(--ink) !important; background: rgba(var(--ink-rgb),.06) !important; }
  .dcv2 .dc-h-agent:hover { border-color: rgba(var(--band-acc-rgb),.6) !important; }
  .dcv2 .dc-h-menurow:hover { background: rgba(var(--ink-rgb),.05) !important; border-color: rgba(var(--ink-rgb),.16) !important; color: var(--ink) !important; }
}

/* Press feedback. The canvas had NO :active state anywhere -- every button,
   card, agent row and nav control was dead on press. A press that produces
   no response reads as an interface that did not hear you, and it is the
   cheapest feel win on the page.

   Ordered after the hover block deliberately: :active and :hover have equal
   specificity and both carry !important (the reveal runtime writes an inline
   transform once an element lands, which outranks a plain class), so source
   order is what lets a press win over a hover. Each rule restates the hover
   lift so pressing a hovered card scales it in place rather than dropping it.

   Scale stays in the 0.95-0.98 band. Large surfaces take the gentle end:
   3% on a full-width card reads as collapsing, not pressing. */
.dcv2 .dc-h-btn-sm:active, .dcv2 .dc-h-btn-lg:active, .dcv2 .dc-h-btn-cyan:active { transform: translateY(-1px) scale(.97) !important; transition-duration: var(--dur-press) !important; }
.dcv2 .dc-h-ghost:active, .dcv2 .dc-h-ghost-light:active { transform: scale(.97) !important; transition-duration: var(--dur-press) !important; }
.dcv2 .dc-h-icon:active, .dcv2 .dc-h-social:active { transform: scale(.94) !important; transition-duration: var(--dur-press) !important; }
.dcv2 .dc-h-card:active, .dcv2 .dc-h-card-dark:active { transform: translateY(-3px) scale(.985) !important; transition-duration: var(--dur-press) !important; }
.dcv2 .dc-h-agent:active, .dcv2 .dc-h-menurow:active, .dcv2 .dc-h-intent:active { transform: scale(.985) !important; transition-duration: var(--dur-press) !important; }

/* Anything pressable that had no transition of its own now has one, so the
   press has something to animate along. Naming the properties keeps this off
   'all', which would drag unintended ones off the GPU.

   !important, and measured rather than assumed: most of these elements carry an
   inline transition: "transform .2s, box-shadow .2s" that names no timing
   function, so it falls back to CSS default ease. Inline beats class, so
   without !important the curve below never applied to the CTAs at all -- a
   browser read still returned ease after the token landed. The :active rules
   above outrank this on specificity, so press duration still wins. */
.dcv2 [class*="dc-h-"] { transition-property: transform, color, background, border-color, box-shadow, opacity !important; transition-duration: var(--dur-hover) !important; transition-timing-function: var(--ease-out) !important; }

/* Footer social tiles. Band tokens, not literals: the footer follows the theme
   in both themes, so a token here would invert with the page. */
.dcv2 .dc-h-social:hover { border-color: rgba(var(--band-acc-rgb),.6) !important; background: rgba(var(--band-acc-rgb),.14) !important; color: var(--band-acc) !important; }
.dcv2 .dc-h-intent:hover { border-color: rgba(56,229,255,.45) !important; background: rgba(56,229,255,.06) !important; }

/* The pricing comparison table's "scrolls sideways" hint is only true on a
   narrow viewport. Inline styles cannot carry a media query, so the page tags
   the note and the breakpoint lives here. */
@media (min-width: 640px) { .dcv2 .dc-pricing-scroll-note { display: none; } }

/* ─── Responsive structures ───────────────────────────────────────────────
   Inline styles cannot carry a media query and this canvas keeps almost
   everything inline, so the few structures that genuinely need a breakpoint
   are declared here, beside the pricing note that was already doing it.

   All of them are authored narrow-first: the single-column case is the base
   rule and the wide layout is the enhancement. That order is not a style
   preference. The About principles list was written the other way round, as a
   bare 'minmax(0,17rem) minmax(0,1fr)' with no breakpoint at all, and on a
   375px viewport the first track took 263px and the body column resolved to
   0px — so every paragraph in the longest section on the page rendered one
   word per line and ran off the right edge. Declaring the narrow case first
   makes that class of failure impossible: a breakpoint that never matches now
   leaves a readable page rather than a broken one. */

/* A label/body definition row. Used by About for both the engine's parts and
   the principles list. */
.dcv2 .dc-defrow { display: grid; grid-template-columns: minmax(0,1fr); gap: 8px; }
@media (min-width: 760px) { .dcv2 .dc-defrow { grid-template-columns: minmax(0,17rem) minmax(0,1fr); gap: 12px 40px; } }

/* The engine's parts carry a leading numeral, so their label track is wider
   than a plain definition row's and earns a later breakpoint. */
/* Two columns, not three. The old middle column held a title of roughly 110px
   inside 15rem and the third held a 62ch body inside whatever was left, which
   at the figure width stranded a few hundred pixels of gutter between a name
   and the sentence that describes it. Title and body now share one cell.

   align-items: start, so a plate sits at the top of its row beside the name it
   belongs to rather than floating against a body of variable length. */
.dcv2 .dc-engine-row { display: grid; grid-template-columns: minmax(0,1fr); gap: 14px; }
@media (min-width: 760px) { .dcv2 .dc-engine-row { grid-template-columns: 168px minmax(0,1fr); gap: 0 28px; align-items: start; } }

/* The duotone plate beside each part of the engine.
   The image supplies luminance only; the hue comes from the gradient beneath
   it, and that gradient is built from theme tokens. So one grayscale file
   reads correctly in both themes and there is no second export to drift —
   which is the failure that got the previous pair of images deleted.
   Anchoring the far stop on --paper is what makes the plate recede into the
   page ground rather than sit on it as a tile. */
.dcv2 .dc-plate {
  position: relative; isolation: isolate; overflow: hidden;
  border-radius: 4px; height: 128px;
  background: linear-gradient(150deg, var(--acc2) 0%, var(--paper) 88%);
}
/* One fixed size for all three, which is what makes them read as a set rather
   than as three unrelated pictures. Stretching each plate to its own row was
   tried and is worse twice over: height:100% on a stretched item in an
   auto-sized row is circular, and Blink resolves it by inflating the row to
   250px+, and even sized correctly a texture that tall beside four lines of
   text dominates the words it is supposed to accompany. */
@media (min-width: 760px) { .dcv2 .dc-plate { height: 112px; align-self: start; } }
.dcv2 .dc-plate img {
  display: block; width: 100%; height: 100%; object-fit: cover;
  mix-blend-mode: luminosity; opacity: .9;
}

/* About's hero: the figure sits beside the text only once there is genuinely
   room for both, and stacks under it before either has to be squeezed. */
.dcv2 .dc-about-hero { display: grid; grid-template-columns: minmax(0,1fr); gap: 52px; align-items: center; }
@media (min-width: 980px) { .dcv2 .dc-about-hero { grid-template-columns: minmax(0,1.02fr) minmax(0,.98fr); gap: 64px; } }

/* ─── Nav below 900px ──────────────────────────────────────────────────────
   The bar was a single flex row with 'white-space: nowrap' and no breakpoint,
   so on a phone the links ran past the right edge and were clipped — every
   page on this canvas was unnavigable on mobile, not just one.

   This block is the one exception to the narrow-first order above, and the
   reason is the same reasoning pointing the other way. Everything else fails
   safe when narrow is the base; navigation does not. If the breakpoint never
   applies — an old engine, a print or reader context, a test environment that
   does not evaluate media queries — a narrow-first nav resolves to
   'display: none' and the visitor is left with no way off the page at all,
   with the links also dropped from the accessibility tree. Declared this way
   round the same failure leaves a cramped bar, which is recoverable.

   The links are NOT duplicated into a separate mobile menu. There is one set
   of anchors in the document and this only changes where they are laid out,
   because a second copy would put two "Solutions" links in the <nav> and the
   suite's scoped getByRole("link") queries would match both. */
.dcv2 .dc-navlinks {
  display: flex; flex-direction: row; align-items: center;
  gap: clamp(14px, 2.2vw, 26px);
  font-size: 13.5px; font-weight: 500; white-space: nowrap;
}
.dcv2 .dc-navtoggle { display: none; }

@media (max-width: 899.98px) {
  .dcv2 .dc-navlinks {
    display: none;
    position: absolute; top: 68px; left: 0; right: 0;
    flex-direction: column; align-items: stretch;
    gap: 2px; padding: 10px 20px 18px;
    background: var(--paper);
    border-bottom: 1px solid rgba(var(--ink-rgb),.12);
    box-shadow: 0 24px 50px rgba(9,9,7,.16);
    font-size: 15px; white-space: normal;
  }
  .dcv2 .dc-navlinks[data-open="true"] { display: flex; }
  /* Comfortable touch targets once the row becomes a stacked list. The padding
     goes on the About Us wrapper rather than on its button, so that row is the
     same height as the anchors beside it — on the button it left About Us
     visibly shorter than every other row in the list. */
  .dcv2 .dc-navlinks > a, .dcv2 .dc-navlinks > div { padding: 12px 2px; }
  /* The About Us panel floats over the page from the bar, but inside the
     stacked panel there is nothing to float over — it flows in place. */
  .dcv2 .dc-navlinks .dc-aboutpanel { position: static; transform: none; padding-top: 4px; }
  .dcv2 .dc-navlinks .dc-aboutpanel > div { width: auto; box-shadow: none; background: var(--paper2); }
  .dcv2 .dc-navtoggle { display: inline-flex; }
  .dcv2 .dc-navtag { display: none; }
}

/* ─── /about/ceo ───────────────────────────────────────────────────────────
   The page runs the canvas's own live particle field behind a portrait held in
   orbit. Only what needs a breakpoint or a state lives here. */

/* Text left, portrait right, and the two overlap once there is room: the
   headline's last lines run under the portrait's glow rather than stopping
   politely beside it. Narrow-first, so with no breakpoint they simply stack. */
.dcv2 .dc-ceo-stage { display: grid; grid-template-columns: minmax(0,1fr); gap: 44px; align-items: center; }
@media (min-width: 960px) {
  .dcv2 .dc-ceo-stage { grid-template-columns: minmax(0,1.06fr) minmax(0,0.94fr); gap: 0; }
  /* The overlap. The portrait column pulls left into the text column, and the
     text keeps its own stacking context above the glow but below the print. */
  .dcv2 .dc-ceo-portrait { margin-left: -86px; }
}

/* The portrait answers the pointer as one object rather than as a photo that
   scales: the halo blooms, both orbits light, and the print lifts. Grouped on
   the wrapper so the whole assembly responds together — the effect is that the
   field around him intensifies, which is the page's own idea rather than a
   generic image hover.
   !important for the reason every hover rule in this file carries it: the
   reveal observer writes an inline transform on the group once it lands, and
   an inline value outranks a class.
   The orbits get border-color only. They are mid-animation on dcv2-spinSlow,
   and transitioning a property an animation already owns fights the keyframes. */
.dcv2 .dc-ceo-halo { opacity: .82; transition: opacity .5s ease, transform .5s cubic-bezier(.22,1,.36,1); }
.dcv2 .dc-ceo-print { transition: transform .5s cubic-bezier(.22,1,.36,1), box-shadow .5s ease; }
.dcv2 .dc-ceo-orbit, .dcv2 .dc-ceo-orbit-outer { transition: border-color .5s ease; }
.dcv2 .dc-ceo-portrait:hover .dc-ceo-halo { opacity: 1 !important; transform: scale(1.08) !important; }
.dcv2 .dc-ceo-portrait:hover .dc-ceo-orbit { border-color: rgba(56,229,255,.58) !important; }
.dcv2 .dc-ceo-portrait:hover .dc-ceo-orbit-outer { border-color: rgba(56,229,255,.3) !important; }
.dcv2 .dc-ceo-portrait:hover .dc-ceo-print { transform: translateY(-9px) !important; box-shadow: 0 44px 92px rgba(9,9,7,.44) !important; }

/* Pagination cells and segmented-control options. Hover only on the ones that
   are actually a destination — a disabled arrow or the already-selected
   segment must not light up, or the control promises a move it will not make.
   :not(:disabled) and [aria-checked="false"] carry that. */
.dcv2 .dc-pg-cell:not(:disabled):not([aria-current="page"]):hover {
  border-color: rgba(var(--ink-rgb),.4) !important;
  background: rgba(var(--ink-rgb),.05) !important;
  color: var(--ink) !important;
}
.dcv2 .dc-seg[aria-checked="false"]:hover {
  background: rgba(var(--ink-rgb),.06) !important;
  color: var(--ink) !important;
}

/* Record chips. Three at rest, wrapping rather than shrinking below legibility. */
.dcv2 .dc-ceo-chips { display: grid; grid-template-columns: minmax(0,1fr); gap: 12px; }
@media (min-width: 720px) { .dcv2 .dc-ceo-chips { grid-template-columns: repeat(3, minmax(0,1fr)); } }

/* A chip lifts and its rule lights on hover. !important for the same reason
   every other hover rule here carries it: the reveal observer writes an inline
   transform once the element lands, and inline outranks a class. */
.dcv2 .dc-ceo-chip { transition: transform .25s, border-color .25s, background .25s; }
.dcv2 .dc-ceo-chip:hover { transform: translateY(-4px) !important; border-color: rgba(56,229,255,.5) !important; background: rgba(56,229,255,.05) !important; }

/* -- The one remaining light: the hero's own ------------------------------
   Sections used to carry their own pair of cyan blooms, positioned at
   negative offsets inside overflow:clip. The clip sliced each one while it
   was still near half its peak, and that straight cut is what read as a
   gradient stuck in the corner. Lighting every section also meant the same
   glow repeated down both edges of the page, which at any zoomed-out view
   was decoration multiplied rather than atmosphere.

   They are all gone. The hero keeps its composed scene, because that is a
   deliberate focal composition rather than section decoration, and this is
   the curve its two blooms decay on: --dc-bloom-a is the peak alpha, and
   the stops step it down on an inverse-square-ish decay, so at half radius
   the light sits at 30% rather than the 50% a linear ramp would give. */
.dcv2 .dc-bloom,
.dc-bloom {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(closest-side,
    rgba(56,229,255, var(--dc-bloom-a, .12)) 0%,
    rgba(56,229,255, calc(var(--dc-bloom-a, .12) * .62)) 26%,
    rgba(56,229,255, calc(var(--dc-bloom-a, .12) * .30)) 48%,
    rgba(56,229,255, calc(var(--dc-bloom-a, .12) * .11)) 68%,
    rgba(56,229,255, calc(var(--dc-bloom-a, .12) * .03)) 84%,
    rgba(56,229,255, 0) 100%);
}

/* -- Seams: a plane change, not a line ----------------------------------
   The three band sections resolve their ground from --band now. In light mode
   that is near-black against cream and the hard edge is the design: a dramatic
   slab, left crisp, --dc-seam-h at 0. In dark mode paper is already near-black
   so the band can only whisper, and any hard edge at that contrast reads as a
   rendering artefact. There --dc-seam-h opens up and the two grounds cross
   over a long ramp instead.

   The ramp is eased rather than linear because the eye detects the DERIVATIVE
   of a gradient, not its value: a straight interpolation between two colours
   still shows a shoulder at each end (Mach banding). The stops below flatten
   towards zero slope at both ends so nothing resolves as an edge. */
/* isolation, so the ramp below can sit at z-index -1 safely. A positioned
   element with z-index 0 paints ABOVE non-positioned in-flow content, so the
   first version of this would have covered the text of any band section whose
   content wrapper was not itself positioned. Dropping to -1 puts it above the
   section's own background and below everything else, but -1 only stays inside
   the section if the section is a stacking context, and position:relative
   alone is not one. isolation:isolate makes it one for the cost of a keyword,
   which is what lets this class be applied to any section without first
   auditing how its children are positioned. */
.dcv2 .dc-seam {
  isolation: isolate;
}
.dcv2 .dc-seam::before,
/* A band whose lower neighbour is the FOOTER does not ramp downward.

   The footer is a hard dark slab in both themes, deliberately its own plane,
   so a boundary against it is a real edge rather than a seam to dissolve. That
   was invisible while the band was also near-black; once the band gained a
   light state the ramp became a 130px smear from cream into near-black, which
   is the one place a cross-over reads worse than a line. */
.dcv2 .dc-seam-foot::after { display: none; }

.dcv2 .dc-seam::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: var(--dc-seam-h, 0px);
  pointer-events: none;
  z-index: -1;
}
.dcv2 .dc-seam::before {
  top: 0;
  background: linear-gradient(to bottom,
    var(--paper) 0%,
    color-mix(in srgb, var(--band) 10%, var(--paper)) 20%,
    color-mix(in srgb, var(--band) 40%, var(--paper)) 44%,
    color-mix(in srgb, var(--band) 74%, var(--paper)) 68%,
    color-mix(in srgb, var(--band) 93%, var(--paper)) 86%,
    var(--band) 100%);
}
.dcv2 .dc-seam::after {
  bottom: 0;
  background: linear-gradient(to top,
    var(--dc-seam-next, var(--paper)) 0%,
    color-mix(in srgb, var(--band) 10%, var(--dc-seam-next, var(--paper))) 20%,
    color-mix(in srgb, var(--band) 40%, var(--dc-seam-next, var(--paper))) 44%,
    color-mix(in srgb, var(--band) 74%, var(--dc-seam-next, var(--paper))) 68%,
    color-mix(in srgb, var(--band) 93%, var(--dc-seam-next, var(--paper))) 86%,
    var(--band) 100%);
}

/* -- Capabilities ---------------------------------------------------------
   Narrow-first, because the previous grid was a fixed repeat(6, 1fr) with no
   media query: on a phone that put three span-2 cards side by side at roughly
   120px each. One column is the base, and the wider layouts are the override.

   Type is set as a SET rather than by size alone -- weight, size and leading
   move together, and tracking is size-specific: tight and negative on the
   display heading, which reads too loose as it grows, and back to neutral on
   body copy, which does not. */
.dcv2 .dc-cap-head {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 44px;
}
.dcv2 .dc-cap-h2 {
  margin: 0;
  font-family: 'Manrope', sans-serif;
  font-weight: 600;
  font-size: clamp(28px, 3.4vw, 46px);
  letter-spacing: -0.025em;
  line-height: 1.06;
  max-width: 17ch;
  text-wrap: balance;
}
.dcv2 .dc-cap-lede {
  margin: 0;
  max-width: 46ch;
  font-size: 16px;
  line-height: 1.6;
  color: rgba(var(--ink-rgb),.66);
}

@media (min-width: 900px) {
  /* The lede sits beside the heading from here, the split the rest of the
     canvas uses. Baseline-aligned to the heading's LAST line rather than
     bottom-aligned to its box, so the two settle on one reading line. */
  .dcv2 .dc-cap-head {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: 48px;
    margin-bottom: 52px;
  }
  .dcv2 .dc-cap-lede { max-width: 38ch; text-align: right; }
}

.dcv2 .dc-cap-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  perspective: 900px;
}
@media (min-width: 640px) {
  .dcv2 .dc-cap-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  /* Three into two leaves an orphan, so the first spans both and leads. */
  .dcv2 .dc-cap-grid > *:first-child:nth-last-child(3) { grid-column: 1 / -1; }
}
@media (min-width: 960px) {
  .dcv2 .dc-cap-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .dcv2 .dc-cap-grid > *:first-child:nth-last-child(3) { grid-column: auto; }
  .dcv2 .dc-cap-grid-quiet { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.dcv2 .dc-cap-grid-quiet { margin-top: 14px; }

/* A capability: a filled surface that lifts. Material weight is what separates
   the two registers now, in place of the numerals and dots that came out. */
.dcv2 .dc-cap {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 26px 26px 24px;
  border-radius: 18px;
  background: var(--paper2);
  border: 1px solid rgba(var(--ink-rgb),.13);
  min-height: 188px;
}

/* A guarantee: open, under a hairline. Quieter on purpose -- a guarantee is a
   different kind of claim than a capability, and it should not compete. */
.dcv2 .dc-cap-quiet {
  background: transparent;
  border: 0;
  border-top: 1px solid rgba(var(--ink-rgb),.18);
  border-radius: 0;
  padding: 24px 2px 4px;
  min-height: 0;
}

.dcv2 .dc-cap-t {
  margin: 0;
  font-family: 'Manrope', sans-serif;
  font-weight: 650;
  font-size: 19px;
  letter-spacing: -0.015em;
  line-height: 1.25;
}
.dcv2 .dc-cap-b {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.58;
  color: rgba(var(--ink-rgb),.66);
  max-width: 44ch;
}
/* Pushed to the foot so every card in a row lands its link on one line. */
.dcv2 .dc-cap-a {
  margin-top: auto;
  padding-top: 14px;
  align-self: flex-start;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.005em;
}
/* Keeps margin-top:auto from the base rule rather than overriding it, so the
   two guarantee links land on one line even though one body wraps to two and
   the other does not. Grid stretches both cards to the row height, which is
   what gives auto something to push against. */
.dcv2 .dc-cap-quiet .dc-cap-a { padding-top: 18px; padding-bottom: 2px; }

/* The link covers the whole card, so the entire surface is pressable while the
   accessible tree still sees one link with one name. */
.dcv2 .dc-cap-a::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
}
.dcv2 .dc-cap-a:focus-visible { outline: none; }
.dcv2 .dc-cap-a:focus-visible::after {
  outline: 2px solid var(--acc2);
  outline-offset: 3px;
}

/* The arrow steps forward with the card, gated so a tap on a phone does not
   leave it stuck mid-travel. */
.dcv2 .dc-cap-arrow {
  display: inline-block;
  margin-left: .4em;
  transition: transform var(--dur-hover) var(--ease-out);
}
@media (hover: hover) and (pointer: fine) {
  .dcv2 .dc-cap:hover .dc-cap-arrow { transform: translateX(3px); }
  .dcv2 .dc-cap-quiet:hover { border-top-color: rgba(var(--ink-rgb),.4); }
}
.dcv2 .dc-cap-quiet { transition: border-color var(--dur-hover) var(--ease-out); }
.dcv2 .dc-cap-quiet:active { transform: scale(.99); }

/* -- The bar ---------------------------------------------------------------
   A translucent material with content scrolling under it, rather than an
   opaque strip with a rule beneath. The separation is the material, and it
   only asserts itself once there is something to separate FROM: at the top of
   the page nothing is underneath, so the bar sits at its lightest and carries
   no divider at all.

   This replaces a cyan highlight that swept the bottom edge on a 4.5s loop.
   A thin coloured bar travelling horizontally under the top chrome is the
   universal indeterminate progress bar, so the nav read as permanently
   loading -- a metaphor doing real damage, not a taste call.

   saturate() alongside the blur because blurring alone greys out whatever is
   behind it; pushing saturation back keeps the page's colour alive through
   the material. */
.dcv2 .dc-nav {
  background: color-mix(in srgb, var(--paper) 74%, transparent);
  -webkit-backdrop-filter: blur(10px) saturate(155%);
  backdrop-filter: blur(10px) saturate(155%);
  border-bottom: 1px solid transparent;
  transition:
    background var(--dur-hover) var(--ease-out),
    -webkit-backdrop-filter var(--dur-hover) var(--ease-out),
    backdrop-filter var(--dur-hover) var(--ease-out),
    border-color var(--dur-hover) var(--ease-out);
}
.dcv2 .dc-nav[data-stuck] {
  background: color-mix(in srgb, var(--paper) 90%, transparent);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  backdrop-filter: blur(18px) saturate(180%);
  border-bottom-color: rgba(var(--ink-rgb),.1);
}

/* The scroll edge: a short gradient under the bar instead of a hard 1px line
   doing all the separating. Sits outside the bar so it falls across the
   content, and fades rather than draws. */
.dcv2 .dc-nav::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 22px;
  pointer-events: none;
  opacity: 0;
  background: linear-gradient(to bottom, rgba(var(--ink-rgb),.055), rgba(var(--ink-rgb),0));
  transition: opacity var(--dur-hover) var(--ease-out);
}
.dcv2 .dc-nav[data-stuck]::after { opacity: 1; }

/* Translucency is a preference, not a given. */
@media (prefers-reduced-transparency: reduce) {
  .dcv2 .dc-nav {
    background: var(--paper);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
}

/* A thread in a list. The whole row is the target, so the hover is on the row
   rather than on the title, and it is gated: on touch it fires on tap and then
   stays applied, which on a list leaves one row looking selected. */
@media (hover: hover) and (pointer: fine) {
  .dcv2 .dc-thread-row:hover { background: rgba(var(--ink-rgb),.04); }
}
.dcv2 .dc-thread-row { transition: background var(--dur-hover) var(--ease-out); }
.dcv2 .dc-thread-row:active { background: rgba(var(--ink-rgb),.07); transition-duration: var(--dur-press); }
.dcv2 .dc-thread-row:first-child { border-top: 0 !important; }

/* Underline tabs and the segmented sort beside them. Both are filters on the
   list under them, so the active one is marked by attachment (an underline) or
   by a raised chip, not by colour alone. */
.dcv2 .dc-tabline {
  border-bottom: 2px solid transparent;
  color: rgba(var(--ink-rgb),.62);
  transition: color var(--dur-hover) var(--ease-out), border-color var(--dur-hover) var(--ease-out);
}
.dcv2 .dc-tabline[data-active] { border-bottom-color: var(--acc2); color: var(--ink); }
@media (hover: hover) and (pointer: fine) {
  .dcv2 .dc-tabline:not([data-active]):hover { color: var(--ink); }
}

.dcv2 .dc-seg {
  color: rgba(var(--ink-rgb),.62);
  background: transparent;
  transition: color var(--dur-hover) var(--ease-out), background var(--dur-hover) var(--ease-out);
}
.dcv2 .dc-seg[data-active] {
  background: var(--paper2);
  color: var(--ink);
  box-shadow: 0 1px 2px rgba(var(--ink-rgb),.08);
}
.dcv2 .dc-seg:active { transform: scale(.97); transition-duration: var(--dur-press); }

/* A topic chip. It is a toggle, so the pressed state is a filled accent rather
   than a colour shift a reader has to compare against its neighbours. */
.dcv2 .dc-tagchip {
  background: rgba(var(--ink-rgb),.06);
  color: rgba(var(--ink-rgb),.62);
  transition: background var(--dur-hover) var(--ease-out), color var(--dur-hover) var(--ease-out);
}
.dcv2 .dc-tagchip[data-active] { background: var(--acc2); color: var(--btnfg); }
@media (hover: hover) and (pointer: fine) {
  .dcv2 .dc-tagchip:not([data-active]):hover { color: var(--ink); background: rgba(var(--ink-rgb),.1); }
}
.dcv2 .dc-tagchip:active { transform: scale(.96); transition-duration: var(--dur-press); }

/* The auth panel image. Same duotone the About plates use: one grayscale file
   blended for luminance over a gradient built from theme tokens, so a single
   asset reads correctly in both themes and there is no second export to drift. */
.dcv2 .dc-auth-plate {
  isolation: isolate;
  background: linear-gradient(160deg, rgba(var(--acc2-rgb),.55) 0%, var(--paper) 92%);
}
.dcv2 .dc-auth-plate img {
  display: block; width: 100%; height: 100%;
  object-fit: cover; object-position: 60% 35%;
  mix-blend-mode: luminosity; opacity: .85;
}

/* Reduced motion means fewer and GENTLER animations, not none. The previous
   rule killed every transition on the canvas, including the opacity and colour
   changes that carry meaning and cause nobody vestibular discomfort. What
   actually needs to stop is movement: parallax, lifts, scroll reveals sliding
   in, and anything looping. */
@media (prefers-reduced-motion: reduce) {
  .dcv2 *, .dcv2 *::before, .dcv2 *::after {
    animation: none !important;
    transition-property: opacity, color, background-color, background, border-color, box-shadow, filter !important;
    transition-duration: 160ms !important;
  }
  .dcv2 [data-plx] { transform: none !important; }
  .dcv2 [class*="dc-h-"]:hover, .dcv2 [class*="dc-h-"]:active { transform: none !important; }
}
`;
